import { SECONDS_PER_HOUR } from "@/config/constants";
import type { Canton } from "@/types/insurance";
import {
  getPublicHolidaysInMonth,
  getCompanyClosureDaysInMonth,
  isPublicHoliday,
  isCompanyClosure,
  type SwissHoliday,
} from "@/utils/swissHolidays";

// ─── Rounding ───────────────────────────────────────────────────────

/** Round to 2 decimal places. SSOT — use everywhere instead of inline Math.round. */
export function round2(n: number): number {
  return Math.round(n * 100) / 100;
}

/** Round to 2 decimals and clamp to >= 0. */
export function clampMin0(n: number): number {
  return Math.max(0, round2(n));
}

// ─── Time conversion ────────────────────────────────────────────────

/** Convert a duration stored in seconds to hours. */
export function secondsToHours(seconds: number): number {
  return seconds / SECONDS_PER_HOUR;
}

// ─── Business day computation ───────────────────────────────────────

/** Count business days (Mon-Fri) in a month, ignoring holidays. */
export function getBusinessDaysInMonth(year: number, month: number): number {
  const daysInMonth = new Date(year, month, 0).getDate(); // month is 1-based
  let count = 0;
  for (let day = 1; day <= daysInMonth; day++) {
    const dow = new Date(year, month - 1, day).getDay();
    if (dow !== 0 && dow !== 6) count++;
  }
  return count;
}

/** Count working days in a month = business days minus holidays minus company closures. */
export function getWorkingDaysInMonth(
  year: number,
  month: number,
  canton: Canton | string,
  includeClosures = true
): number {
  const totalBiz = getBusinessDaysInMonth(year, month);
  const holidays = getPublicHolidaysInMonth(year, month, canton).filter((h) => {
    const dow = h.date.getDay();
    return dow !== 0 && dow !== 6; // only count holidays that fall on a weekday
  }).length;
  const closures = includeClosures
    ? getCompanyClosureDaysInMonth(year, month, canton)
    : 0;
  return totalBiz - holidays - closures;
}

/**
 * Count working days between `from` and `to` (inclusive) in a given month,
 * respecting holidays and company closures.
 */
export function getWorkingDaysInRange(
  from: Date,
  to: Date,
  canton: Canton | string,
  includeClosures = true
): number {
  let count = 0;
  const cur = new Date(from);
  while (cur <= to) {
    const dow = cur.getDay();
    if (dow !== 0 && dow !== 6 && !isPublicHoliday(cur, canton)) {
      if (!includeClosures || !isCompanyClosure(cur)) {
        count++;
      }
    }
    cur.setDate(cur.getDate() + 1);
  }
  return count;
}

// ─── Employee contract parameters (SSOT) ───────────────────────────

import type { EmployeeOverride } from "@/config/employeeOverrides";

/**
 * Derive monthly-target and related parameters from a DB employee record,
 * with optional overrides from config/employeeOverrides.ts.
 *
 * Precedence for monthlyTarget:
 *   DB monthly_target_hours > override.monthlyTargetHours > weeklyHours × 52/12
 *
 * The monthlyTarget is always prorated by (workingDays / businessDays) in
 * calculateMonthlySoll — months with public holidays get a lower Soll.
 *
 * This is the SSOT — every file that needs monthlyTarget must call this.
 */
export interface ContractParams {
  /** Expected hours per (full) month — will be prorated by working days */
  monthlyTarget: number;
  /** Canton for holiday lookup */
  canton: Canton | string;
  /** Contract start, or undefined if not set */
  contractStart: Date | undefined;
  /** Employment end (last working day), or undefined if still employed */
  contractEnd: Date | undefined;
}

export function deriveContractParams(
  employee: {
    monthly_target_hours?: number | null;
    working_hours_per_week?: number | null;
    tax_canton?: string | null;
    employment_start_date?: string | null;
    employment_end_date?: string | null;
  },
  overrides?: EmployeeOverride,
): ContractParams {
  // monthly target: DB column > config override > weekly conversion
  const monthlyTarget = employee.monthly_target_hours
    ?? overrides?.monthlyTargetHours
    ?? round2((employee.working_hours_per_week ?? 0) * 52 / 12);

  const canton: Canton | string = employee.tax_canton || "ZH";

  // employment start: config override wins when DB value is clearly wrong
  const startStr = overrides?.employmentStartDate ?? employee.employment_start_date;
  const contractStart = startStr ? new Date(startStr) : undefined;

  // employment end: config override wins when DB value is missing
  const endStr = overrides?.employmentEndDate ?? employee.employment_end_date;
  const contractEnd = endStr ? new Date(endStr) : undefined;

  return { monthlyTarget, canton, contractStart, contractEnd };
}

// ─── Soll (expected hours) computation ──────────────────────────────

export interface MonthlySollResult {
  /** Expected working hours for the month (after prorating & vacation) */
  soll: number;
  /** Total weekdays in the month */
  businessDays: number;
  /** Public holidays falling on a weekday in this month */
  holidays: SwissHoliday[];
  /** Company closure days (weekdays, not already holidays) */
  closureDays: number;
  /** Actual workable days (businessDays - holidays - closures) */
  workingDays: number;
}

/**
 * Dynamically compute the monthly Soll (expected hours).
 *
 * Formula: monthlyTarget × (workingDays / totalBusinessDays)
 * — then prorate for partial months (contract start / current month)
 * — then subtract vacation hours.
 *
 * This replaces the old hardcoded MONTHLY_HOURS_OVERRIDES.
 */
export function calculateMonthlySoll(
  monthlyTarget: number,
  year: number,
  month: number, // 1-based
  canton: Canton | string,
  options?: {
    includeClosures?: boolean;
    /** If the contract started mid-month, only count from this date */
    contractStart?: Date;
    /** For the current (in-progress) month, prorate up to this date */
    currentDate?: Date;
    /** Vacation hours taken in this month (subtracted from Soll) */
    vacationHours?: number;
    /** Month is unpaid leave → Soll = 0 */
    isUnpaidLeave?: boolean;
  }
): MonthlySollResult {
  const includeClosures = options?.includeClosures ?? true;

  if (options?.isUnpaidLeave) {
    return {
      soll: 0,
      businessDays: getBusinessDaysInMonth(year, month),
      holidays: getPublicHolidaysInMonth(year, month, canton),
      closureDays: includeClosures ? getCompanyClosureDaysInMonth(year, month, canton) : 0,
      workingDays: 0,
    };
  }

  const totalBiz = getBusinessDaysInMonth(year, month);
  const holidayList = getPublicHolidaysInMonth(year, month, canton).filter((h) => {
    const dow = h.date.getDay();
    return dow !== 0 && dow !== 6;
  });
  const closureCount = includeClosures
    ? getCompanyClosureDaysInMonth(year, month, canton)
    : 0;
  const totalWorkingDays = totalBiz - holidayList.length - closureCount;

  // Determine the effective range within the month
  const monthStart = new Date(year, month - 1, 1);
  const monthEnd = new Date(year, month, 0); // last day of month

  let effectiveStart = monthStart;
  let effectiveEnd = monthEnd;

  if (options?.contractStart && options.contractStart > monthStart) {
    effectiveStart = options.contractStart;
  }
  if (options?.currentDate && options.currentDate < monthEnd) {
    effectiveEnd = options.currentDate;
  }

  // Count working days in the effective range
  const effectiveWorkingDays = getWorkingDaysInRange(
    effectiveStart,
    effectiveEnd,
    canton,
    includeClosures
  );

  // Prorate: if the effective range covers the full month, ratio = 1
  const ratio = totalWorkingDays > 0 ? effectiveWorkingDays / totalWorkingDays : 0;
  const baseSoll = monthlyTarget * (totalWorkingDays / totalBiz) * ratio;
  const vacation = options?.vacationHours ?? 0;
  const soll = clampMin0(baseSoll - vacation);

  return {
    soll,
    businessDays: totalBiz,
    holidays: holidayList,
    closureDays: closureCount,
    workingDays: effectiveWorkingDays,
  };
}
